import { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { Helmet } from "react-helmet";
import Footer from "../../components/Footer";
import { submitLeadProductRequest } from "../../services/api";
import "./styles.scss";

const CATEGORY_OPTIONS = [
  {
    value: "smartphones",
    label: "Smartphones",
  },
  {
    value: "notebooks",
    label: "Notebooks",
  },
  {
    value: "Smart TVs",
    label: "Smart TVs",
  },
  {
    value: "eletrodomesticos",
    label: "Eletrodomésticos",
    helper: "Ex.: máquina de lavar, micro-ondas, fritadeiras...",
  },
  {
    value: "cosmeticos",
    label: "Cosméticos e produtos de beleza",
  },
  {
    value: "casa_cozinha_construcao",
    label: "Casa, cozinha ou construção",
  },
  {
    value: "outros",
    label: "Outros",
  },
];

const INITIAL_VALUES = {
  name: "",
  phone: "",
  product: "",
  category: "",
};

export default function ProductRequestForm() {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const feedbackRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);


  }, []);

  useEffect(() => {
    if (!isPrivacyModalOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsPrivacyModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isPrivacyModalOpen]);

  const getCategoryLabel = (categoryValue) => {
    const option = CATEGORY_OPTIONS.find((item) => item.value === categoryValue);
    return option ? option.label : "";
  };

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value || value.trim().length === 0) {
          return "Informe seu nome";
        }
        if (value.trim().length < 2) {
          return "O nome deve ter pelo menos 2 caracteres";
        }
        return "";
      case "phone": {
        if (!value || value.trim().length === 0) {
          return "Informe seu WhatsApp";
        }
        const digits = value.replace(/\D/g, "");
        if (digits.length < 11) {
          return "Informe o número com DDD";
        }
        return "";
      }
      case "product":
        if (!value || value.trim().length === 0) {
          return "Descreva o produto desejado";
        }
        if (value.trim().length < 3) {
          return "Use pelo menos 3 caracteres";
        }
        return "";
      case "category":
        if (!value) {
          return "Selecione uma categoria";
        }
        return "";
      default:
        return "";
    }
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (touched[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: validateField(field, value),
      }));
    }
  };

  const handleBlur = (field) => (event) => {
    const { value } = event.target;
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = Object.keys(values).reduce((acc, field) => {
      const error = validateField(field, values[field]);
      if (error) {
        acc[field] = error;
      }
      return acc;
    }, {});

    setErrors(nextErrors);
    setTouched({
      name: true,
      phone: true,
      product: true,
      category: true,
    });

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await submitLeadProductRequest({
        name: values.name.trim(),
        phone: values.phone,
        product_name: values.product.trim(),
        category: getCategoryLabel(values.category),
      });

      setFeedback({
        status: "success",
        message:
          "Recebemos o seu pedido! Assim que encontrarmos ofertas com a sua cara, avisaremos por WhatsApp.",
        at: Date.now(),
      });
      setValues(INITIAL_VALUES);
      setTouched({});
      setErrors({});
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.category?.[0] ||
        error?.response?.data?.errors?.phone?.[0];

      setFeedback({
        status: "error",
        message:
          responseMessage ||
          "Não foi possível enviar agora. Tente novamente em instantes ou fale com a nossa equipe.",
        at: Date.now(),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!feedbackRef.current || !feedback) {
      return;
    }

    feedbackRef.current.scrollIntoView({ behavior: "smooth", block: "center" });

    try {
      feedbackRef.current.focus({ preventScroll: true });
    } catch (error) {
      feedbackRef.current.focus();
    }
  }, [feedback]);

  return (
    <>
      <Helmet title="Jersu Indica: Pedir Produto" />
      <div className="product-request">
        <div className="product-request__inner">
          <section className="product-request__banner" aria-labelledby="product-request-title">
            <div className="product-request__banner-content">
              <span className="product-request__badge">Cobertura completa de ofertas – 2025</span>
              <h1 className="product-request__banner-title">Black Friday Jersu Indica</h1>
              <p className="product-request__banner-text">
                Garimpamos ofertas reais todos os dias para você comprar com segurança e pagar ainda menos.
              </p>
            </div>
          </section>

          <section className="product-request__form-card">
            <header className="product-request__header">
              <h2 id="product-request-title" className="product-request__title">
                Formulário de Ofertas
              </h2>
              <p className="product-request__description">
                Tá pensando em comprar algum produto na Black Friday? Deixa que a gente te ajuda a pagar barato e a fazer uma compra com segurança! 😉
              </p>
            </header>

            {feedback && (
              <div
                ref={feedbackRef}
                className={`product-request__feedback product-request__feedback--${feedback.status}`}
                role="alert"
                aria-live="assertive"
                tabIndex={-1}
              >
                {feedback.message}
              </div>
            )}

            <form className="product-request__form" onSubmit={handleSubmit} noValidate>
              <div className="product-request__field-group">
                <label className="product-request__label" htmlFor="name">
                  Seu nome<span aria-hidden="true" className="product-request__required">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Ex.: Maria"
                  className={`product-request__input${errors.name ? " product-request__input--error" : ""}`}
                  value={values.name}
                  onChange={handleChange("name")}
                  onBlur={handleBlur("name")}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? "name-error" : undefined}
                  maxLength={120}
                />
                {errors.name && touched.name && (
                  <span id="name-error" className="product-request__error">
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="product-request__field-group">
                <label className="product-request__label" htmlFor="phone">
                  Seu WhatsApp
                  <span aria-hidden="true" className="product-request__required">*</span>
                  <span className="product-request__helper">(enviaremos as ofertas para você)</span>
                </label>
                <InputMask
                  mask="(99) 99999-9999"
                  value={values.phone}
                  onChange={handleChange("phone")}
                  onBlur={handleBlur("phone")}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="Ex: (11) 9 9999-9999"
                      className={`product-request__input${errors.phone ? " product-request__input--error" : ""}`}
                      aria-invalid={Boolean(errors.phone)}
                      aria-describedby={errors.phone ? "phone-error" : undefined}
                    />
                  )}
                </InputMask>
                {errors.phone && touched.phone && (
                  <span id="phone-error" className="product-request__error">
                    {errors.phone}
                  </span>
                )}
              </div>

              <div className="product-request__field-group">
                <label className="product-request__label" htmlFor="product">
                  Escreva em poucas palavras o que você gostaria de comprar em oferta
                  <span aria-hidden="true" className="product-request__required">*</span>
                </label>
                <input
                  id="product"
                  name="product"
                  type="text"
                  placeholder="máquinaquina de lavar Colormaq"
                  className={`product-request__input${errors.product ? " product-request__input--error" : ""}`}
                  value={values.product}
                  onChange={handleChange("product")}
                  onBlur={handleBlur("product")}
                  aria-invalid={Boolean(errors.product)}
                  aria-describedby={errors.product ? "product-error" : undefined}
                  maxLength={255}
                />
                {errors.product && touched.product && (
                  <span id="product-error" className="product-request__error">
                    {errors.product}
                  </span>
                )}
              </div>

              <fieldset
                className={`product-request__field-group product-request__field-group--radios${errors.category && touched.category ? " product-request__field-group--error" : ""
                  }`}
                aria-describedby={
                  errors.category && touched.category ? "category-error" : undefined
                }
              >
                <legend className="product-request__label">
                  Qual categoria melhor se encaixa no produto que você quer?
                  <span aria-hidden="true" className="product-request__required">*</span>
                </legend>
                <div className="product-request__radio-group" role="radiogroup" aria-required="true">
                  {CATEGORY_OPTIONS.map((option) => (
                    <label key={option.value} className="product-request__radio-option">
                      <input
                        type="radio"
                        name="category"
                        value={option.value}
                        checked={values.category === option.value}
                        onChange={handleChange("category")}
                        onBlur={handleBlur("category")}
                      />
                      <span className="product-request__radio-label">
                        {option.label}
                        {option.helper && (
                          <em className="product-request__radio-helper">{option.helper}</em>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
                {errors.category && touched.category && (
                  <span className="product-request__error" id="category-error">
                    {errors.category}
                  </span>
                )}
              </fieldset>

              <p className="product-request__privacy-text">
                Ao continuar, você concorda com nossa
                <button
                  type="button"
                  className="product-request__privacy-link"
                  onClick={() => setIsPrivacyModalOpen(true)}
                >
                  política de privacidade
                </button>
                , em respeito à LGPD.
              </p>

              <button
                type="submit"
                className="product-request__submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Enviando..." : "Enviar"}
              </button>
            </form>
          </section>
        </div>
      </div>
      {isPrivacyModalOpen && (
        <div className="product-request__privacy-modal" role="dialog" aria-modal="true">
          <div className="product-request__privacy-modal-content">
            <button
              type="button"
              className="product-request__privacy-close"
              onClick={() => setIsPrivacyModalOpen(false)}
              aria-label="Fechar política de privacidade"
            >
              ×
            </button>
            <h3 className="product-request__privacy-modal-title">Política de privacidade</h3>
            <p>
              Ao enviar suas informações neste formulário, você autoriza a coleta e utilização de seus dados pessoais básicos
              (nome e telefone), nos autorizando a entrar em contato com você via WhatsApp, para atividades relacionadas aos
              grupos de ofertas e canais das empresas Caçadores de Ofertas e/ou Jersu Indica Ofertas, de forma gratuita e sem
              prazo determinado.
            </p>
            <p>
              O envio das informações é completamente voluntário, e você poderá revogar esta autorização a qualquer momento,
              entrando em contato conosco pelo endereço de e-mail contatojersu@gmail.com.
            </p>
            <p>
              Declaramos que a utilização será sempre respeitosa e vinculada às atividades do grupo, sem qualquer
              desvirtuamento da sua finalidade, em conformidade com a Lei Geral de Proteção de Dados Pessoais.
            </p>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}
